import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, RotateCcw, X, Bookmark } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useObjectUrl } from "@/hooks/useObjectUrl";
import { prepareImage, PHOTO_ERROR_MSG } from "@/lib/image";
import { VerdictCopyService } from "@/lib/verdicts";
import UploadZone from "@/components/UploadZone";
import AnalyzingSequence from "@/components/AnalyzingSequence";
import CompleteAndSeeRoom from "@/components/CompleteAndSeeRoom";

export default function RoomCheck() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("upload"); // upload | analyzing | result
  const [file, setFile] = useState(null);
  const { url: preview, setFromFile, clear } = useObjectUrl();
  const [res, setRes] = useState(null); // {check_id, image_url, result, source, directions}
  const [saved, setSaved] = useState(false);

  const verdict = useMemo(() => (res ? VerdictCopyService.pickHome(res.result.score) : null), [res]);

  const saveSpace = async () => {
    if (!user) { toast("Sign in to save to My Home."); navigate("/signin"); return; }
    const rr = res.result;
    try {
      await api.post("/home/space", { room_type: res.room_type, check_id: res.check_id, image_url: res.image_url,
        score: rr.score, style_blend: rr.style_blend, result: rr, source: res.source,
        directions: res.directions, palette: rr.palette });
      setSaved(true); toast.success("Saved to My Home.");
    } catch { toast.error("Couldn't save this room."); }
  };

  const onFile = async (f) => {
    try { const p = await prepareImage(f, { maxDim: 2000 }); setFile(p); setFromFile(p); analyze(p); }
    catch { toast.error(PHOTO_ERROR_MSG); }
  };
  const retake = () => { setFile(null); clear(); setRes(null); setStep("upload"); };

  const analyze = async (f) => {
    setStep("analyzing");
    try {
      const form = new FormData(); form.append("file", f);
      const { data } = await api.post("/home/room/analyze", form);
      if (data.status !== "ok") { toast.error(data.message || "Couldn't read that space."); setStep("upload"); return; }
      setRes(data); setStep("result");
    } catch { toast.error(PHOTO_ERROR_MSG); setStep("upload"); }
  };

  const deletePhoto = async () => {
    if (res?.check_id && user) { try { await api.delete(`/outfit-check/photo/${res.check_id}`); } catch { /* noop */ } }
    setRes((r) => ({ ...r, check_id: null, image_url: null }));
    toast.success("Room photo deleted.");
  };

  const r = res?.result;
  return (
    <div className="max-w-4xl mx-auto px-5 md:px-8 py-12 md:py-16 min-h-[70vh]">
      {step === "upload" && (
        <div className="text-center max-w-lg mx-auto">
          <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E]">MOIRÉ Home · Room Check</p>
          <h1 className="font-serif text-4xl md:text-5xl text-[#F5F2EA] mt-2">How's my space?</h1>
          <p className="font-serif italic text-lg text-[#F5F2EA]/70 mt-3">Show MOIRÉ your room — we'll tell you what works and what we'd change.</p>
          <div className="mt-8"><UploadZone onFile={onFile} label="Snap your room" testid="room-upload" preview={preview} onClear={() => { setFile(null); clear(); }} /></div>
          <p className="font-sans text-xs text-[#8A8170] mt-5">Best with a wide, well-lit shot. No need for people in frame — your photo stays private.</p>
        </div>
      )}

      {step === "analyzing" && <AnalyzingSequence stages={["Looking at the space…", "Reading the colours and materials…", "Checking the balance…", "Styling the room…"]} />}

      {step === "result" && r && (
        <div>
          <div className="text-center relative">
            <p className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170]">MOIRÉ Home Score</p>
            <div className="font-serif text-6xl text-[#C8A97E]" data-testid="room-score">{Number(r.score).toFixed(1)}<span className="text-2xl text-[#8A8170]"> / 10</span></div>
            {verdict && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-2 relative inline-block">
                {verdict.tier === "exceptional" && <span className="absolute -top-3 -right-6"><Sparkles size={20} className="text-[#E5C293]" /></span>}
                <h2 className={`font-serif tracking-tight ${verdict.tier === "exceptional" ? "text-5xl md:text-6xl verdict-shimmer" : "text-4xl text-[#C8A97E]"}`} data-testid="room-verdict">{verdict.signature || verdict.reaction}</h2>
              </motion.div>
            )}
            {verdict?.signature && <p className="font-serif italic text-2xl text-[#F5F2EA] mt-2">{verdict.reaction}</p>}
            <p className="font-sans text-xs text-[#8A8170] mt-2">{r.style_blend}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mt-9">
            <div>
              <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E] mb-3">What works</p>
              <div className="space-y-3" data-testid="room-works">
                {r.what_works.map((w, i) => (<div key={i}><p className="font-sans text-[9px] tracking-luxe uppercase text-[#8A8170]">{w.title}</p><p className="font-serif text-lg text-[#F5F2EA]/90 leading-snug">{w.note}</p></div>))}
                {r.what_works.length === 0 && <p className="font-serif italic text-[#F5F2EA]/70">A solid base to build on.</p>}
              </div>
            </div>
            <div>
              <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E] mb-3">What I'd change</p>
              <div className="space-y-3" data-testid="room-change">
                {r.what_to_change.map((w, i) => (<div key={i}><p className="font-sans text-[9px] tracking-luxe uppercase text-[#8A8170]">{w.title}</p><p className="font-serif text-lg text-[#F5F2EA]/90 leading-snug">{w.note}</p></div>))}
                {r.what_to_change.length === 0 && <p className="font-serif italic text-[#F5F2EA]/70">Honestly? Very little.</p>}
              </div>
            </div>
          </div>

          <div className="border border-[#F5F2EA]/10 p-5 mt-6" data-testid="room-one-change">
            <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E] mb-1">If I changed one thing…</p>
            <p className="font-serif text-xl text-[#F5F2EA]">{r.one_change.title}</p>
            <p className="font-serif italic text-base text-[#F5F2EA]/80 mt-1">{r.one_change.note}</p>
          </div>

          {r.palette?.length > 0 && (
            <div className="mt-6" data-testid="room-palette">
              <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E] mb-3">Colour story</p>
              <div className="flex flex-wrap gap-3">
                {r.palette.map((c, i) => (<div key={i} className="text-center"><span className="block h-11 w-11 rounded-full border border-[#F5F2EA]/15" style={{ backgroundColor: c.hex }} /><span className="block font-sans text-[9px] tracking-luxe uppercase text-[#8A8170] mt-1.5">{c.name}</span></div>))}
              </div>
            </div>
          )}

          {res.directions?.length > 0 && <CompleteAndSeeRoom mode="room" checkId={res.check_id} source={res.source} directions={res.directions} />}

          <div className="flex flex-wrap gap-3 justify-center mt-10">
            <button onClick={saveSpace} disabled={saved} data-testid="room-save-space" className="flex items-center gap-2 bg-[#C8A97E] text-[#0D0D0D] px-5 py-2.5 font-sans text-xs tracking-luxe uppercase hover:bg-[#E5C293] disabled:opacity-60 transition-colors"><Bookmark size={13} strokeWidth={1.6} /> {saved ? "Saved to My Home" : "Save to My Home"}</button>
            {res.image_url && <button onClick={deletePhoto} data-testid="room-delete-photo" className="flex items-center gap-2 border border-[#F5F2EA]/15 px-5 py-2.5 font-sans text-xs tracking-luxe uppercase text-[#8A8170] hover:text-[#F5F2EA] transition-colors"><X size={13} /> Delete Photo</button>}
            <button onClick={retake} className="flex items-center gap-2 border border-[#F5F2EA]/15 px-5 py-2.5 font-sans text-xs tracking-luxe uppercase text-[#F5F2EA]/70 hover:border-[#C8A97E] transition-colors"><RotateCcw size={13} /> Check Another Room</button>
          </div>
        </div>
      )}
    </div>
  );
}
